import type {
    CompanyFunction,
    ContactExpandOpts,
    IContactPerson,
    IContactPersonRelation,
    IContactResponse,
    IContactSingleResponse,
    IFetchContactParams,
} from '@condo/domain';
import { getClient } from '@/shared/api/client';

export interface IAssignContactRelation {
    relationName?: string;
    contactId: number;
    estateId?: number;
    mfhId?: number;
    mfhPortfolioId?: number;
    companyFunction?: CompanyFunction;
}

export const saveContact = async (contact: Partial<IContactPerson>, relation?: Partial<IContactPersonRelation>): Promise<IContactPerson> => {
    if (contact.contactId) {
        return getClient('basecamp')
            .put(`/contacts/${contact.contactId}`, { contact })
            .then(response => response.data.contact);
    }
    return getClient('basecamp')
        .post('/contacts', { contact, relation })
        .then(response => response.data.contact);
};

export const getContacts = async (params?: IFetchContactParams): Promise<IContactResponse> =>
    getClient('basecamp')
        .get('/contacts', { params })
        .then(response => response.data);

export const getMfhContacts = async (mfhId: number): Promise<IContactPerson[]> =>
    getClient('basecamp')
        .get<{ contacts: IContactPerson[] }>(`/mfh/${mfhId}/contacts`)
        .then(response => response.data.contacts);

export const getOneContact = async (contactId: number | 'me', expandOpts?: ContactExpandOpts): Promise<IContactSingleResponse> =>
    getClient('basecamp')
        .get(`/contacts/${contactId}`, { params: { expand: expandOpts } })
        .then(response => response.data);

export const deleteContact = async (contactId: number): Promise<void> => getClient('basecamp').delete(`/contacts/${contactId}`);

export const assignContact = async (data: { relation: IAssignContactRelation }): Promise<{ relation: IContactPerson }> =>
    getClient('basecamp')
        .post('/contact-relation', data)
        .then(res => res.data.relation);

export const removeContactAssignment = async (contactRelationId: number): Promise<void> =>
    getClient('basecamp').delete(`/contact-relation/${contactRelationId}`);

export const mergeContacts = async (contact: IContactPerson, contacts: IContactPerson[]): Promise<IContactSingleResponse> => {
    const contactsPayload = contacts.map(c => (c.contactId === contact.contactId ? { ...c, ...contact } : c));

    return getClient('basecamp')
        .put(`/contacts/${contact.contactId}/merge`, { contacts: contactsPayload })
        .then(response => response.data);
};

export const syncSlackAvatar = async (contactId: number): Promise<IContactPerson> =>
    getClient('basecamp')
        .post(`/contacts/${contactId}/sync-slack-avatar`)
        .then(response => response.data.contact);
